import { useQuery } from 'react-query';
import { authFetch } from '../../../CommonUtils/authFetch';
import { urlWithParams } from '../../../CommonUtils/urlWithParams';
import { queryKeys } from './queryKeys';
import { useClientId } from '../../../_hooks/useClientId';

export enum TrustType {
  Revocable,
  Irrevocable,
}

export enum TrustGrantor {
  Principal,
  Partner,
  SomebodyElse,
}

export enum TrusteeType {
  Principal,
  Spouse,
  Other,
}

export enum BeneficiaryType {
  Primary,
  Contingent,
}

export interface BeneficiaryDTO {
  id: string;
  name: string;
  percentage: number;
  type: BeneficiaryType;
}

export interface TrustDTO {
  Id: string;
  Name: string;
  TrustType: TrustType;
  Grantors: { Value: TrustGrantor; Label: string }[];
  IsGstExempt: boolean;
  Situs: string | null;
  Trustees: { Value: number; Label: string }[];
  DatedDate: string | null;
  Beneficiaries: {
    beneficiaries: BeneficiaryDTO[];
    lastBeneficiaryUpdate: string | null;
  };
  IncludeInGrossEstate: boolean;
  LiquidAssetDistributionStrategy: { PercentageAtFirstSpouseDeath: number } | null;
  IlliquidAssetDistributionStrategy: { AssetsAtFirstSpouseDeath: string[] } | null;
  IlliquidAssetOptions: { Id: string; Name: string }[];
}

export interface TrustsDTO {
  Items: TrustDTO[];

  TrustTypeOptions: {
    label: string;
    value: TrustType;
  }[];

  GrantorOptions: {
    label: string;
    value: TrustGrantor;
  }[];

  BeneficiariesOptions: {
    Id: string;
    Name: string;
  }[];

  TrusteeOption: {
    label: string;
    value: TrusteeType;
  }[];

  HasSpouse: boolean;
}

export const FETCH_TRUSTS_ENDPOINT = '/Consumer/Trusts';

interface FetchTrustsParams {
  clientId: number;
}

const fetchTrusts = async ({ clientId }: FetchTrustsParams): Promise<TrustsDTO> => {
  return authFetch(urlWithParams(FETCH_TRUSTS_ENDPOINT, { clientId }));
};

interface TrustsQueryParams {
  enabled?: boolean;
}

export const useTrustsQuery = ({ enabled }: TrustsQueryParams = { enabled: true }) => {
  const clientId = useClientId();

  return useQuery(
    queryKeys.trusts(clientId),

    () => fetchTrusts({ clientId }),

    {
      cacheTime: 0,
      enabled,
    },
  );
};
